import { merge } from 'layeredmerge'
import { DataBase } from '@/lib/kysely'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { useStageStore } from '@/stores/stage'
import type { DMLQuery } from '@/lib/entity/base'

export type Names = 'BASE' | 'STAGE'
type Mapping = [Names, (layer: Names, value: any) => string][]
type Proc<T> = (db: Kysely<DB>) => Promise<T>

export const defaultMapping: Mapping = [
  ['BASE', (layer: any, value: any) => `BASE:${value}`],
  ['STAGE', (layer: any, value: any) => `STAGE:${value}`]
]

async function getDMLQueries() {
  const stagedEntity = Array.from(useStageStore().getAll().values())
  const dmls: DMLQuery[] = []
  for (const entity of stagedEntity) {
    if (!entity.isModified() && !entity.isDeleted) continue
    if (!(await entity.isValidAsync())) continue
    dmls.push(...(await entity.mutateLocalDB()))
  }
  return dmls
}

function processWithRollback<T extends Record<string, any>>(dmls: DMLQuery[], proc: Proc<T>) {
  return withRollback(async (trx) => {
    // important note!
    // トランザクションの中は極力短くすること。特に await はしないこと。
    for (const query of dmls) {
      await trx.executeQuery(query)
    }
    return proc(trx)
  })
}

/**
 * Stageを適応する前・後、それぞれのDBに対して、proc を実行する
 */
export async function layered<T extends Record<string, any>>(
  proc: Proc<T>,
  mapping = defaultMapping
) {
  const mapper = new Map(mapping)
  const dmls = await getDMLQueries()
  const base = proc(DataBase)

  // DML が無い場合 = STAGEがない場合は無駄にクエリを実行しない
  if (dmls.length === 0) {
    const awaitedBase = await base
    const res = merge<Names>(mapper)(awaitedBase, awaitedBase)
    return res
  }

  const stage = processWithRollback(dmls, proc)
  const objects = await Promise.all([base, stage])
  const res = merge<Names>(mapper)(...objects)
  return res
}

/**
 * Stageを適応後のデータベースの値を返す
 */
export async function withStage<T extends Record<string, any>>(proc: Proc<T>) {
  const dmls = await getDMLQueries()
  // DML が無い場合 = STAGEがない場合は無駄にクエリを実行しない
  if (dmls.length === 0) {
    const base = proc(DataBase)
    return await base
  }
  const stage = processWithRollback(dmls, proc)
  return await stage
}

class RollbackJump extends Error {}

/**
 * Stageを適応した後に proc を実行し、結果を返却
 */
async function withRollback<T>(proc: Proc<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    DataBase.transaction()
      .execute(async (trx) => {
        await proc(trx).then(resolve, reject)
        throw new RollbackJump('rollbacked')
      })
      .catch((e: Error) => {
        if (e instanceof RollbackJump) {
          /* noting to do if rollback jump */
        } else {
          throw e
        }
      })
  })
}
